/*
 * Created Date: 05-09-2022, 11:38:00 pm
 * Author: Peter
 * Email: phantrung696@gmail.com
 * -----
 * Last Modified:
 * Modified By:
 * -----
 * Copyright (c) 2022 PT CORP, Inc
 * -----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	----------------------------------------------------------
 */

import { isAfter, isBefore, isValid } from 'date-fns'
import moment from 'moment'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { convertSectionQuestionText } from './Question'

export const REGEX = {
	CODE: /^[a-z0-9A-Z.đĐ_-]+$/,
	FILL_QUESTION: /\[_*_\]/g,
	LINK_TAG: /<a onclick="event\.preventDefault\(\);[\s\S]*?<\/a>/gi,
	PHONE: /(84|0[3|5|7|8|9])+([0-9]{8})\b/g,
	// eslint-disable-next-line no-useless-escape
	EMAIL: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
	IMAGE_URL: /\.(jpeg|jpg|gif|png)$/,
	// eslint-disable-next-line no-useless-escape
	URL: /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/,
	YOUTUBE_URL:
		/^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)$/,
	PASSWORD: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/
}

Yup.addMethod(
	Yup.string,
	'code',
	function (
		errorMessage = "Chỉ được phép nhập ký tự số, chữ không dấu, các ký tự '-' '_' '.' và không có khoảng cách"
	) {
		const { t } = useTranslation('common')
		return this.matches(REGEX.CODE, {
			message: t(errorMessage),
			excludeEmptyString: true
		})
	}
)

Yup.addMethod(
	Yup.string,
	'password',
	function (
		errorMessage = 'Password must be at least 8 characters, start with a capital letter, include a number, a special character, and contain no spaces'
	) {
		return this.test('PASSWORD', function (value) {
			const checkValidate = /^[A-Z]/.test(value?.[0]) && REGEX.PASSWORD.test(value)

			return checkValidate
				? true
				: this.createError({
						message: errorMessage,
						excludeEmptyString: true
				  })
		})
	}
)

Yup.addMethod(Yup.string, 'phone', function (errorMessage = 'Số điện thoại sai định dạng') {
	const { t } = useTranslation('common')
	return this.matches(REGEX.PHONE, {
		message: t(errorMessage),
		excludeEmptyString: true
	})
})

Yup.addMethod(Yup.string, 'imageUrl', function (errorMessage = 'Invalid image URL format') {
	return this.matches(REGEX.IMAGE_URL, {
		message: errorMessage,
		excludeEmptyString: true
	})
})

Yup.addMethod(Yup.string, 'isUrl', function (errorMessage = 'Invalid URL format') {
	return this.matches(REGEX.URL, {
		message: errorMessage,
		excludeEmptyString: true
	})
})

Yup.addMethod(Yup.string, 'videoUrl', function (errorMessage = 'Invalid video URL format') {
	const finalRegex = new RegExp(`${new RegExp(REGEX.YOUTUBE_URL).source}`)
	return this.matches(finalRegex, {
		message: errorMessage,
		excludeEmptyString: true
	})
})

Yup.addMethod(Yup.date, 'mustBefore', function (dependentPath) {
	return this.test('MUST_BEFORE', function (value) {
		const dependentValue = this?.parent?.[dependentPath]

		if (isValid(value) && isValid(dependentValue)) {
			const check = isBefore(value, dependentValue)
			return check
				? true
				: this.createError({
						message: `Must be before ${moment(dependentValue).format('L')}`
				  })
		}

		return true
	})
})

Yup.addMethod(Yup.date, 'mustAfter', function (dependentPath) {
	return this.test('MUST_AFTER', function (value) {
		const dependentValue = this?.parent?.[dependentPath]

		if (isValid(value) && isValid(dependentValue)) {
			const check = isAfter(value, dependentValue)

			return check
				? true
				: this.createError({
						message: `Must be after ${moment(dependentValue).format('L')}`
				  })
		}

		return true
	})
})

Yup.addMethod(Yup.string, 'maxQuestion', function (max) {
	const { t } = useTranslation('common')
	return this.test(`max-question`, function (value) {
		const data = convertSectionQuestionText(value, t) ?? []
		const totalQs = data.reduce((result, item) => {
			return result + item.questions.correctQuestions.length
		}, 0)

		if (totalQs > max) {
			return this.createError({
				message: t(`Tối đa {{max}} câu hỏi`, { max }),
				excludeEmptyString: true
			})
		}
		return true
	})
})

export const useYupChangeLocale = () => {
	const { t } = useTranslation('common')

	useEffect(() => {
		Yup.setLocale({
			mixed: {
				required: t('yup.mixed.required')
			},
			string: {
				required: t('yup.mixed.required'),
				email: t('yup.string.email'),
				max: ({ max }) => t('yup.string.max', { number: max }),
				min: ({ min }) => t('yup.string.min', { number: min })
			},
			array: {
				min: t('yup.mixed.required')
			},
			number: {
				default: 'test',
				required: t('yup.mixed.required'),
				min: ({ min }) => t('yup.number.min', { min }),
				max: ({ max }) => t('yup.number.max', { max }),
				moreThan: ({ more }) => t('yup.number.more_than', { more }),
				integer: () => t('yup.number.integer')
			},
			date: {
				typeError: t('yup.date.invalid')
			}
		})
	}, [t])
}

export default Yup
